import { useRef, useState } from 'react';
import { useForm } from '@refinedev/react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '@refinedev/core';
import logger from '@/core/services';
import {
  StudyFormSchema,
  StudyFormValues,
  StudyEditFormProps,
  PublishFormSchema,
} from '../types/study-edit.interface';
import { HttpError } from '@refinedev/core';
import { type StudyDetail } from '@aphp-ema/common/study';
import { removeEmptyStrings } from '@/shared/utils/formatting';
import { usePublishStudy } from '@/features/studies/hooks/usePublishStudy';
import { DEFAULT_FORM_VALUES } from '@/features/studies/constants/study-create.constants';
import { Resolver } from 'react-hook-form';

export const useStudyEditForm = ({
  id,
}: {
  id: string;
}): StudyEditFormProps => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();
  const { mutate: publishStudy, isLoading: isPublishing } = usePublishStudy();

  const shouldPublish = useRef(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const navigateToOnSuccess = useRef<string>('/studies');

  const handleMutationSuccess = (data: { data: StudyDetail }) => {
    const returnedStudyId = data.data.id;

    if (shouldPublish.current) {
      displayShowPublishModal();
      return;
    }

    // Early return for error case
    if (!returnedStudyId) {
      openNotification?.({
        type: 'error',
        message: t('notifications.unexpectedError'),
      });
      navigate('/studies');
      return;
    }

    // Default success case
    openNotification?.({
      type: 'success',
      message: t('notifications.studyUpdated'),
    });
    navigate(navigateToOnSuccess.current);
  };

  const resolver: Resolver<StudyFormValues> = async (
    values,
    context,
    options,
  ) => {
    if (shouldPublish.current) {
      return zodResolver(PublishFormSchema)(values, context, options);
    }

    return zodResolver(StudyFormSchema)(values, context, options);
  };

  const form = useForm<StudyDetail, HttpError, StudyFormValues>({
    mode: 'onChange',
    resolver,
    warnWhenUnsavedChanges: false,
    defaultValues: DEFAULT_FORM_VALUES,
    refineCoreProps: {
      resource: 'admin/studies',
      action: 'edit',
      id,
      redirect: false,
      successNotification: false,
      onMutationSuccess: handleMutationSuccess,
      queryOptions: {
        onError: (error: HttpError) => {
          logger.error('Error updating study:', error);
          openNotification?.({
            type: 'error',
            message: t('notifications.errorCreatingStudy'),
          });
        },
      },
    },
  });
  const {
    getValues,
    trigger,
    handleSubmit,
    refineCore: { onFinish, query },
  } = form;

  const submitValues = () => {
    const values = removeEmptyStrings(getValues());
    if (isCompleteStudyFormValues(values)) {
      onFinish(values);
    }
  };

  const saveAndNavigate = async (navigateTo: string) => {
    shouldPublish.current = false;

    const isValid = await trigger();
    if (!isValid) {
      return false;
    }

    navigateToOnSuccess.current = navigateTo;
    await onFinish(getValues());

    return true;
  };

  const displayShowPublishModal = () => {
    const values = getValues();
    const targetPopulation = values.targetPopulation;

    // Check if target population exists and has data
    if (
      !targetPopulation ||
      !targetPopulation.data ||
      targetPopulation.data.length === 0
    ) {
      openNotification?.({
        type: 'error',
        message: t('pages.studies.publish.noTargetPopulation'),
      });
      return;
    }

    setShowPublishModal(true);
  };

  const handleSave = async () => {
    shouldPublish.current = false;
    submitValues();
  };

  const handlePublish = async () => {
    shouldPublish.current = true;

    if (!(await trigger())) {
      return;
    }

    submitValues();
  };

  const handleConfirmPublish = () => {
    publishStudy(id, {
      onSuccess: () => {
        openNotification?.({
          type: 'success',
          message: t('notifications.studyValidatedAndPublished'),
        });
        shouldPublish.current = false;
        setShowPublishModal(false);
        navigate('/studies');
      },
      onError: (error: HttpError) => {
        openNotification?.({
          type: 'error',
          description: t('notifications.errorValidatingStudy'),
          message:
            error.response.data.message || t('notifications.unexpectedError'),
        });
      },
    });
  };

  const handleCancelPublish = () => {
    setShowPublishModal(false);
    shouldPublish.current = false;
  };

  const getPatientCount = (): number => {
    const values = getValues();
    const targetPopulation = values.targetPopulation;
    return targetPopulation?.data?.length || 0;
  };

  const saveButtonProps = {
    ...form.saveButtonProps,
    onClick: () => handleSubmit(handleSave)(),
  };

  const publishButtonProps = {
    onClick: handlePublish,
    loading: isPublishing,
  };

  const publishModalProps = {
    open: showPublishModal,
    onClose: handleCancelPublish,
    onConfirm: handleConfirmPublish,
    patientCount: getPatientCount(),
    isLoading: isPublishing,
  };

  return {
    ...form,
    saveButtonProps,
    formLoading: form.refineCore.formLoading,
    studyId: id,
    publishButtonProps,
    isPublishing,
    isFirstLoading: query?.status === 'loading',
    publishModalProps,
    saveAndNavigate,
  };
};

const isCompleteStudyFormValues = (
  values: Partial<StudyFormValues>,
): values is StudyFormValues => {
  return values.name !== undefined;
};
